import type { ToolContentKind, ToolDetailBlock } from '@easytools/tool-content';

export interface ToolContentVersion {
    id: number;
    kind: ToolContentKind;
    language: string;
    version: number;
    state: 'draft' | 'published' | 'archived';
    origin: 'agent' | 'manual' | 'translation' | 'import';
    sourceVersion?: number | null;
    notes?: string | null;
    createdBy?: string | null;
    createdAt: string;
    updatedAt: string;
    publishedBy?: string | null;
    publishedAt?: string | null;
    content?: string | null;
}

export interface ToolContentSlot {
    kind: ToolContentKind;
    language: string;
    published?: ToolContentVersion | null;
    draft?: ToolContentVersion | null;
    isOutdated: boolean;
}

export const CONTENT_KINDS: { kind: ToolContentKind; label: string; hint: string }[] = [
    { kind: 'publicPage', label: 'Pagina pubblica', hint: 'Sul sito, per chi non conosce ancora Semplora.' },
    { kind: 'card', label: 'Scheda nel portale', hint: 'Si apre dal pulsante ? del tool, per chi è già registrato.' },
    { kind: 'guide', label: 'Guida utente', hint: 'Istruzioni passo per passo.' },
];

export const kindLabel = (kind: ToolContentKind) => CONTENT_KINDS.find(k => k.kind === kind)?.label ?? kind;

export const ORIGIN_LABELS: Record<ToolContentVersion['origin'], string> = {
    agent: 'Desktop Agent',
    manual: 'Modifica manuale',
    translation: 'Traduzione',
    import: 'Importata',
};

/** Stato di un contenuto in una lingua, sempre come testo. */
export function slotStatus(slot?: ToolContentSlot): { label: string; tone: 'none' | 'draft' | 'published' | 'outdated' } {
    if (!slot || (!slot.published && !slot.draft)) return { label: 'Assente', tone: 'none' };
    if (slot.isOutdated) return { label: slot.draft ? 'Da aggiornare · bozza' : 'Da aggiornare', tone: 'outdated' };
    if (slot.published && slot.draft) return { label: `Pubblicata v${slot.published.version} · bozza`, tone: 'draft' };
    if (slot.published) return { label: `Pubblicata v${slot.published.version}`, tone: 'published' };
    return { label: 'Bozza', tone: 'draft' };
}

const dateFormat = new Intl.DateTimeFormat('it-IT', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
export const formatWhen = (value?: string | null) => (value ? dateFormat.format(new Date(value)) : '');

export type BlockChange =
    | { type: 'same'; after: ToolDetailBlock }
    | { type: 'added'; after: ToolDetailBlock }
    | { type: 'moved'; after: ToolDetailBlock }
    | { type: 'removed'; before: ToolDetailBlock }
    | { type: 'changed'; before: ToolDetailBlock; after: ToolDetailBlock };

/**
 * Differenze blocco per blocco tra due versioni (sottosequenza comune più lunga).
 * Un blocco rimosso seguito da uno aggiunto dello stesso tipo conta come modificato;
 * un blocco identico rimosso in un punto e aggiunto in un altro conta come spostato.
 */
export function diffBlocks(before: ToolDetailBlock[], after: ToolDetailBlock[]): BlockChange[] {
    const a = before.map(b => JSON.stringify(b));
    const b = after.map(x => JSON.stringify(x));
    const lcs: number[][] = Array.from({ length: a.length + 1 }, () => Array(b.length + 1).fill(0));
    for (let i = a.length - 1; i >= 0; i--)
        for (let j = b.length - 1; j >= 0; j--)
            lcs[i][j] = a[i] === b[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1]);

    const raw: BlockChange[] = [];
    let i = 0, j = 0;
    while (i < a.length || j < b.length) {
        if (i < a.length && j < b.length && a[i] === b[j]) { raw.push({ type: 'same', after: after[j] }); i++; j++; }
        // A parità si toglie prima il vecchio blocco: rimosso + aggiunto dello stesso tipo diventa "modificato".
        else if (i < a.length && (j >= b.length || lcs[i + 1][j] >= lcs[i][j + 1])) { raw.push({ type: 'removed', before: before[i] }); i++; }
        else { raw.push({ type: 'added', after: after[j] }); j++; }
    }

    // Spostamenti: lo stesso blocco esce da un punto ed entra in un altro.
    const removedKeys = new Set(raw.flatMap(c => (c.type === 'removed' ? [JSON.stringify(c.before)] : [])));
    const movedKeys = new Set(raw.flatMap(c => (c.type === 'added' && removedKeys.has(JSON.stringify(c.after)) ? [JSON.stringify(c.after)] : [])));
    const withMoves: BlockChange[] = raw.flatMap((c): BlockChange[] => {
        if (c.type === 'removed' && movedKeys.has(JSON.stringify(c.before))) return [];
        if (c.type === 'added' && movedKeys.has(JSON.stringify(c.after))) return [{ type: 'moved', after: c.after }];
        return [c];
    });

    const merged: BlockChange[] = [];
    for (let k = 0; k < withMoves.length; k++) {
        const current = withMoves[k], next = withMoves[k + 1];
        if (current.type === 'removed' && next?.type === 'added' && current.before.type === next.after.type) {
            merged.push({ type: 'changed', before: current.before, after: next.after });
            k++;
        } else {
            merged.push(current);
        }
    }
    return merged;
}
